//Svelte
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Types
import type { DeviceConditionUI } from 'src/types/DeviceCondAndTriggerUI';
import { type VirtualComponentConfig } from 'src/types/VirtualComponents';
import { type DeviceActionUI } from 'src/types/DeviceActionUI';
//Stores
import { devices_settings } from 'src/stores/device-runtime/status';
//Helpers
import { getVCButtonEvents, getVCIcon } from './virtual_component_utils';
import { translateTemplateTitle } from 'src/helpers/xt1_helpers/template_helpers';

const _format = get(format);
/**
 * Get the settings object for the virtual component from the device settings
 * @param device_id  Real device id
 * @param component Id of the virtual component, e.g. number:200
 * @returns
 */
const _get_vc_component_settings = (device_id: string, component: string): VirtualComponentConfig => {
	const settings = devices_settings.getByRealId(device_id);
	return settings?.[component];
};

/**
 * Translates a value based on the virtual component role for device preview labels.
 *
 * @param {string} value - The value to be condition_data.
 * @param {string} role - The role associated with the value.
 * @returns {string} - The condition_data value.
 */
const _get_vc_value_label = (value: string, role: string) => {
	return role ? translateTemplateTitle(value) : value;
};

/** Get text for the operator/command
 * @param operator - Operator/command - equal, less than, more than OR set for actions
 */
const _operator_to_label = (operator: string) => {
	if (operator === 'set') return ':'; //Actions use set as operator/cmd
	return _format('operator_' + operator);
};

/** Get the user-defined label for the enum value from the VC settings
 * @param component_settings - Virtual component settings
 * @param value - Value of the enum
 */
const _get_vc_enum_value_label = (component_settings: VirtualComponentConfig, value: string) => {
	return component_settings?.meta?.ui?.titles?.[value] || value;
};

/**
 * Get the preview label for virtual component conditions
 *
 * @param condition_data - Condition data object, transliterated from the iots scene
 * @returns - Object with name, icon, iconColor, value
 */
export const get_vc_condition_label = (condition_data: DeviceConditionUI) => {
	const { cond_name, operator } = condition_data;
	let value = condition_data?.value;

	const vc_settings = _get_vc_component_settings(
		condition_data?.device?.id,
		condition_data?.cond_name + ':' + condition_data?.input_index,
	);

	// if (import.meta.env.DEV) {
	// 	console.warn('VC Condition Label', condition_data, vc_settings);
	// }

	return get_vc_preview_label(vc_settings, {
		name: cond_name,
		value,
		operator,
	});
};

/**
 * Get the preview label for virtual component actions
 *
 * @param action_data - Action data object, transliterated from the iots scene
 * @returns - Object with name, icon, iconColor, value
 */
export const get_vc_action_label = (action_data: DeviceActionUI) => {
	const { name, value, index } = action_data.actions?.[0];
	const vc_settings = _get_vc_component_settings(action_data?.device?.id, name + ':' + index);

	// 	console.log('VC Action Label', action_data, vc_settings);

	return get_vc_preview_label(vc_settings, {
		name,
		value,
		operator: 'set',
	});
};

/**
 * Get the preview label for virtual component property
 *
 * @param vc_settings - Virtual component settings
 * @param script_data - Interpreted iots scene action/condition data {name, value, operator}
 * @returns - Object for Preview with name, icon, iconColor, value
 */
const get_vc_preview_label = (
	vc_settings,
	script_data: { name: string; value: string | number; operator: string },
) => {
	let component_type = script_data.name;
	let name = _get_vc_value_label(vc_settings?.name || script_data.name, vc_settings?.role);
	let value = script_data.value;
	let unit = vc_settings?.meta?.ui?.unit || '';
	let web_icon = getVCIcon(vc_settings);
	let operator_label = _operator_to_label(script_data.operator);
	let value_label = name + ' ' + operator_label + ' ' + value + ' ' + unit;

	// if (import.meta.env.DEV) {
	// 	console.log('get_vc_condition_label', name);
	// 	console.log('script_data', script_data);
	// 	console.log('operator_label', operator_label);
	// 	console.log('VC Settings: ', vc_settings);
	// }

	switch (component_type) {
		case 'button':
			let props = getVCButtonEvents(String(value));
			value_label = name + ': ' + _format(`event_${props?.event}_click`);
			return {
				name,
				icon: props?.icon || 'fa fa-hand-back-point-up',
				value: value_label,
			};
		case 'number':
			return {
				name,
				icon: web_icon || 'fa fa-calculator',
				value: value_label,
			};
		case 'boolean':
			value_label =
				name +
				': ' +
				_get_vc_value_label(
					vc_settings?.meta?.ui?.titles?.[value === 'true' ? 1 : 0] || value,
					vc_settings?.role,
				);
			const isTrue = value === 'true';
			return {
				name,
				icon: web_icon || (isTrue ? 'fa-duotone fa-light fa-toggle-on' : 'fa fa-toggle-off'),
				...(isTrue && {
					iconStyle:
						'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
				}),
				...(!isTrue && {
					iconColor: 'var(--color-danger)',
				}),
				value: value_label,
			};
		case 'enum':
			value_label =
				name +
				': ' +
				_get_vc_value_label(
					_get_vc_enum_value_label(vc_settings, String(value)),
					vc_settings?.role,
				);
			return {
				name,
				icon: web_icon || 'fa fa-list',
				value: value_label,
			};
		case 'text':
			value_label = `${name} ${operator_label} "${value}"`;
			return {
				name,
				icon: web_icon || 'fa fa-font',
				value: value_label,
			};
		default:
			return {
				name,
				icon: web_icon || 'fa fa-cube',
				value: value_label,
			};
	}
};

/**
 * Get the condition list item for virtual component conditions preview
 *
 * @param condition_data - Condition data
 * @returns - Object with name, icon, type
 */
export const get_vc_condition_list_item = (condition_data) => {
	let vc_type_label = _format(`virtual_component_${condition_data.cond_name}`, {
		default: condition_data.cond_name,
	});
	//Show Virtual Component
	return {
		name: _format('scene_virtual_component_type', {
			default: `Virtual component: {type}`,
			values: {
				type: vc_type_label,
			},
		}),
		icon: 'fa-solid fa-cube',
		type: 'trigger',
	};
};
